import { useState, useEffect, useMemo, useCallback } from "react";
import toast from "react-hot-toast";
import { getPanel, reservar, cancelar, getMisClases } from "../services/api";
import TenantLogo from "../components/shared/TenantLogo";
import { useTenant } from "../context/TenantContext";

// ─── Tiny helpers ────────────────────────────────────────────────────────────

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function fmtFecha(iso) {
  return new Date(iso).toLocaleDateString("es-AR", {
    weekday: "long", day: "2-digit", month: "long",
  });
}
function fmtHora(iso) {
  return new Date(iso).toLocaleTimeString("es-AR", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
function fmtCorta(iso) {
  return new Date(iso).toLocaleDateString("es-AR", {
    day: "2-digit", month: "short",
  });
}

// ─── Shared UI ───────────────────────────────────────────────────────────────

function GlassCard({ accent, title, subtitle, right, children, className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl border bg-slate-900/60 backdrop-blur-xl shadow-[0_20px_60px_-30px_rgba(0,0,0,0.7)] ${className}`}
      style={{ borderColor: `${accent}22` }}>
      <div className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
      <div className="p-5 sm:p-6">
        {(title || right) && (
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              {title && <h3 className="text-base sm:text-lg font-semibold text-white">{title}</h3>}
              {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
            </div>
            {right && <div className="shrink-0">{right}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function Pill({ children, variant = "neutral", accent, className = "" }) {
  const map = {
    neutral: "border-slate-700 bg-slate-800/40 text-slate-300",
    success: "border-emerald-500/30 bg-emerald-600/15 text-emerald-300",
    warning: "border-amber-500/30 bg-amber-600/10 text-amber-300",
    danger:  "border-red-500/30 bg-red-600/10 text-red-300",
  };
  if (variant === "accent") return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${className}`}
      style={{ borderColor: `${accent}35`, backgroundColor: `${accent}14`, color: accent }}>
      {children}
    </span>
  );
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[variant]} ${className}`}>{children}</span>;
}

function PrimaryBtn({ accent, disabled, onClick, children, className = "", small }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`inline-flex items-center justify-center rounded-xl font-semibold text-white transition
        focus:outline-none disabled:cursor-not-allowed disabled:opacity-40
        ${small ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} ${className}`}
      style={{ background: disabled ? "rgba(71,85,105,0.35)" : `linear-gradient(90deg, ${accent}, ${accent}dd)`, border: "1px solid rgba(148,163,184,0.18)" }}>
      {children}
    </button>
  );
}

function CuposBar({ inscritos, cupos, accent }) {
  const pct = cupos > 0 ? (inscritos / cupos) * 100 : 0;
  const color = pct >= 100 ? "#ef4444" : pct >= 75 ? "#f59e0b" : accent;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{inscritos}/{cupos} inscriptos</span>
        <span style={{ color }}>{cupos - inscritos} libres</span>
      </div>
      <div className="h-1 w-full rounded-full bg-slate-700/60">
        <div className="h-1 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function NotaClase({ nota, accent }) {
  const [open, setOpen] = useState(false);
  if (!nota?.trim()) return null;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 text-xs font-medium transition" style={{ color: accent }}>
        <span>{open ? "▾" : "▸"}</span>
        {open ? "Ocultar rutina" : "Ver rutina del día"}
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-slate-700 bg-slate-800/40 px-3 py-2">
          <p className="text-xs font-medium text-slate-400 mb-1">Rutina / Ejercicios</p>
          <p className="text-xs text-slate-200 whitespace-pre-line leading-relaxed">{nota}</p>
        </div>
      )}
    </div>
  );
}

// ─── Stats card ───────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border p-4 flex flex-col gap-1"
      style={{ borderColor: `${accent}22`, backgroundColor: "rgba(15,23,42,0.35)" }}>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

// ─── Badges ───────────────────────────────────────────────────────────────────

const BADGE_DEFS = [
  { id: "primera", icon: "🎯", label: "Primera clase", desc: "Asististe a tu primera clase", req: s => s.total_clases >= 1 },
  { id: "cinco",   icon: "🔥", label: "5 clases",      desc: "Completaste 5 clases",          req: s => s.total_clases >= 5 },
  { id: "diez",    icon: "💪", label: "10 clases",     desc: "Completaste 10 clases",          req: s => s.total_clases >= 10 },
  { id: "veinte",  icon: "🏆", label: "20 clases",     desc: "Completaste 20 clases",          req: s => s.total_clases >= 20 },
  { id: "mes",     icon: "📅", label: "Mes activo",    desc: "3+ clases este mes",             req: s => s.clases_mes >= 3 },
];

function BadgesSection({ stats, accent }) {
  const earned = BADGE_DEFS.filter(b => b.req(stats));
  const locked = BADGE_DEFS.filter(b => !b.req(stats));
  if (stats.total_clases === 0 && earned.length === 0) return null;
  return (
    <GlassCard accent={accent} title="Logros" subtitle="Tus badges por asistencia">
      <div className="flex flex-wrap gap-3">
        {earned.map(b => (
          <div key={b.id} title={b.desc}
            className="flex flex-col items-center gap-1 rounded-xl border px-4 py-3 text-center cursor-default"
            style={{ borderColor: `${accent}35`, backgroundColor: `${accent}12` }}>
            <span className="text-2xl">{b.icon}</span>
            <span className="text-xs font-semibold" style={{ color: accent }}>{b.label}</span>
          </div>
        ))}
        {locked.slice(0, 3).map(b => (
          <div key={b.id} title={`Bloqueado — ${b.desc}`}
            className="flex flex-col items-center gap-1 rounded-xl border border-slate-700 px-4 py-3 text-center opacity-35 cursor-default">
            <span className="text-2xl grayscale">{b.icon}</span>
            <span className="text-xs font-semibold text-slate-500">{b.label}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ─── Planes (sin suscripción) ─────────────────────────────────────────────────

function PlanesSection({ planes, accent }) {
  const [open, setOpen] = useState(false);
  if (!planes?.length) return null;
  return (
    <GlassCard accent={accent}
      title="Planes disponibles"
      subtitle="Acercate a recepción para contratar uno"
      right={
        <button onClick={() => setOpen(v => !v)}
          className="text-xs font-medium transition" style={{ color: accent }}>
          {open ? "Ocultar ▴" : "Ver planes ▾"}
        </button>
      }>
      {!open && (
        <p className="text-sm text-slate-400">
          Hay <span className="font-semibold text-white">{planes.length}</span> planes disponibles en este gimnasio.
        </p>
      )}
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-1">
          {planes.map(p => (
            <div key={p.id} className="rounded-xl border border-slate-700 bg-slate-800/30 p-4">
              <p className="font-semibold text-white text-base">{p.nombre}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: accent }}>
                ${Number(p.precio).toLocaleString("es-AR")}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400">
                <span className="bg-slate-700/50 rounded-full px-2 py-0.5">{p.creditos} créditos</span>
                <span className="bg-slate-700/50 rounded-full px-2 py-0.5">{p.duracion_dias} días</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

// ─── Filtros de clases ────────────────────────────────────────────────────────

function FiltrosPill({ options, value, onChange, accent }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition ${value === o.value
            ? "" : "border-slate-700 bg-slate-800/30 text-slate-400 hover:text-white hover:border-slate-500"}`}
          style={value === o.value
            ? { borderColor: `${accent}55`, backgroundColor: `${accent}20`, color: accent }
            : {}}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── Vista Calendario ─────────────────────────────────────────────────────────

function CalendarioSemanal({ clases, accent, onReservar, suscripcion, wishlist, toggleWish }) {
  const hoy = new Date();
  // Build 7-day window starting today
  const dias = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(hoy);
    d.setDate(hoy.getDate() + i);
    return d;
  });

  const clasePorDia = dia =>
    clases.filter(c => {
      const cd = new Date(c.fecha_hora);
      return cd.toDateString() === dia.toDateString();
    });

  const sinSus = !suscripcion;
  const sinCred = !!suscripcion && suscripcion.creditos_restantes <= 0;

  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-7 gap-1 min-w-[560px]">
        {dias.map((d, i) => {
          const esHoy = d.toDateString() === hoy.toDateString();
          const clasesDia = clasePorDia(d);
          return (
            <div key={i} className="flex flex-col gap-1">
              <div className={`text-center py-1 rounded-lg text-xs font-semibold ${esHoy ? "text-white" : "text-slate-400"}`}
                style={esHoy ? { backgroundColor: `${accent}25`, color: accent } : {}}>
                <div>{DIAS[d.getDay() === 0 ? 6 : d.getDay() - 1]}</div>
                <div className="text-lg font-bold leading-tight">{d.getDate()}</div>
              </div>
              <div className="flex flex-col gap-1">
                {clasesDia.length === 0 && (
                  <div className="rounded-lg bg-slate-800/20 h-10 flex items-center justify-center text-xs text-slate-600">—</div>
                )}
                {clasesDia.map(c => {
                  const llena = c.cupos_restantes <= 0;
                  const disabled = sinSus || sinCred || llena;
                  const wished = wishlist.has(c.id);
                  return (
                    <div key={c.id}
                      className="rounded-lg border border-slate-700 bg-slate-800/40 p-1.5 text-xs cursor-default hover:bg-slate-800/60 transition"
                      title={`${c.tipo} — ${fmtHora(c.fecha_hora)}`}>
                      <div className="font-semibold text-white truncate">{c.tipo}</div>
                      <div className="text-slate-400">{fmtHora(c.fecha_hora)}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <button onClick={() => toggleWish(c.id)}
                          className="text-base leading-none" title={wished ? "Quitar favorito" : "Guardar favorito"}>
                          {wished ? "⭐" : "☆"}
                        </button>
                        <button
                          onClick={() => !disabled && onReservar(c.id)}
                          disabled={disabled}
                          className="ml-auto text-xs px-1.5 py-0.5 rounded-md font-medium transition"
                          style={disabled
                            ? { background: "rgba(71,85,105,0.3)", color: "#64748b", cursor: "not-allowed" }
                            : { background: `${accent}25`, color: accent }}>
                          {llena ? "Llena" : sinSus ? "🔒" : sinCred ? "🔒" : "✓"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Historial ────────────────────────────────────────────────────────────────

function HistorialClases({ accent }) {
  const [clases, setClases] = useState(null);
  const [open, setOpen] = useState(false);

  const cargar = async () => {
    if (clases !== null) return;
    const data = await getMisClases();
    setClases(Array.isArray(data) ? data : []);
  };

  const toggle = () => {
    setOpen(v => !v);
    cargar();
  };

  return (
    <GlassCard accent={accent} title="Historial de clases" subtitle="Clases que ya tomaste"
      right={
        <button onClick={toggle} className="text-xs font-medium transition" style={{ color: accent }}>
          {open ? "Ocultar ▴" : "Ver historial ▾"}
        </button>
      }>
      {!open && <p className="text-sm text-slate-500">Mostrá tu historial de asistencia.</p>}
      {open && clases === null && <p className="text-sm text-slate-400">Cargando...</p>}
      {open && clases !== null && clases.length === 0 && (
        <p className="text-sm text-slate-500">Todavía no tomaste ninguna clase.</p>
      )}
      {open && clases !== null && clases.length > 0 && (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {clases.map(c => (
            <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-800/30 px-4 py-2.5">
              <div>
                <span className="text-sm font-semibold text-white">{c.tipo}</span>
                <span className="ml-3 text-xs text-slate-400">{fmtFecha(c.fecha_hora)} · {fmtHora(c.fecha_hora)}</span>
              </div>
              <Pill variant="success">✓ Asistida</Pill>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

// ─── Info gimnasio ────────────────────────────────────────────────────────────

/* function GimnasioInfo({ info, accent }) {
  if (!info?.telefono && !info?.email && !info?.direccion) return null;
  return (
    <GlassCard accent={accent} title={info.nombre} subtitle="Información del gimnasio">
      <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
        {info.telefono && (
          <div className="flex items-center gap-2 text-slate-300">
            <span style={{ color: accent }}>📞</span>
            <a href={`tel:${info.telefono}`} className="hover:underline">{info.telefono}</a>
          </div>
        )}
        {info.email && (
          <div className="flex items-center gap-2 text-slate-300">
            <span style={{ color: accent }}>✉️</span>
            <a href={`mailto:${info.email}`} className="hover:underline">{info.email}</a>
          </div>
        )}
        {info.direccion && (
          <div className="flex items-center gap-2 text-slate-300">
            <span style={{ color: accent }}>📍</span>
            <span>{info.direccion}</span>
          </div>
        )}
      </div>
    </GlassCard>
  );
} */

// ─── Panel principal ──────────────────────────────────────────────────────────

export default function Panel({ cliente, onLogout }) {
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [filterTipo, setFilterTipo] = useState("todos");
  const [filterDia, setFilterDia]   = useState("todos");
  const [vista, setVista]           = useState("lista"); // "lista" | "calendario"
  const [wishlist, setWishlist]     = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("wish_clases") || "[]")); }
    catch { return new Set(); }
  });

  const tenantConfig = useTenant();
  const accent = tenantConfig?.color_primario || "#ea580c";

  const fetchPanel = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPanel();
      if (data.error || data.detail) { setError(data.error || data.detail); setPanel(null); }
      else { setPanel(data); setError(null); }
    } catch { setError("Error al cargar el panel"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPanel(); }, [fetchPanel]);

  const toggleWish = useCallback((id) => {
    setWishlist(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem("wish_clases", JSON.stringify([...next]));
      return next;
    });
  }, []);

  const handleReservar = async (claseId) => {
    const t = toast.loading("Reservando...", { style: { borderLeft: `4px solid ${accent}` } });
    try {
      const res = await reservar(claseId);
      toast.dismiss(t);
      if (res.error) toast.error(res.error, { duration: 5000, icon: "❌" });
      else { toast.success("¡Clase reservada!", { icon: "✅", duration: 3000 }); fetchPanel(); }
    } catch { toast.dismiss(t); toast.error("Error inesperado", { duration: 5000 }); }
  };

  const handleCancelar = (id) => {
    toast((toast_t) => (
      <div>
        <p className="font-semibold mb-3">¿Cancelar esta clase?</p>
        <div className="flex gap-2">
          <button onClick={() => { toast.dismiss(toast_t.id); procederCancelacion(id); }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold">Sí, cancelar</button>
          <button onClick={() => toast.dismiss(toast_t.id)}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm">No</button>
        </div>
      </div>
    ), { duration: 10000, icon: "⚠️" });
  };

  const procederCancelacion = async (id) => {
    const t = toast.loading("Cancelando...", { style: { borderLeft: `4px solid ${accent}` } });
    try {
      const res = await cancelar(id);
      toast.dismiss(t);
      if (res.error) toast.error(res.error, { duration: 6000, icon: "⏰", style: { maxWidth: 500 } });
      else { toast.success("Reserva cancelada. Crédito devuelto.", { icon: "✅", duration: 3000 }); fetchPanel(); }
    } catch { toast.dismiss(t); toast.error("Error inesperado", { duration: 5000 }); }
  };

  // ── Filtros ────────────────────────────────────────────────────────────────

  const clases_disponibles = panel?.clases_disponibles || [];
  const clases_reservadas  = panel?.clases_reservadas  || [];

  const tipos = useMemo(() => {
    const set = new Set(clases_disponibles.map(c => c.tipo));
    return ["todos", ...set];
  }, [clases_disponibles]);

  const diasOpts = useMemo(() => {
    const set = new Set(clases_disponibles.map(c =>
      new Date(c.fecha_hora).toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "short" })
    ));
    return ["todos", ...set];
  }, [clases_disponibles]);

  const clasesFiltradas = useMemo(() => {
    return clases_disponibles.filter(c => {
      if (filterTipo !== "todos" && c.tipo !== filterTipo) return false;
      if (filterDia !== "todos") {
        const label = new Date(c.fecha_hora).toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "short" });
        if (label !== filterDia) return false;
      }
      return true;
    });
  }, [clases_disponibles, filterTipo, filterDia]);

  // ── Loading / error ────────────────────────────────────────────────────────

  if (loading && !panel) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
          style={{ borderColor: `${accent}40`, borderTopColor: "transparent" }} />
        <p style={{ color: accent }} className="font-semibold">Cargando...</p>
      </div>
    </div>
  );

  if (error || !panel) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-900/50 border border-red-500/20 rounded-2xl p-8 max-w-md text-center">
        <h3 className="text-xl font-bold text-white mb-2">Error</h3>
        <p className="text-red-300 mb-4">{error}</p>
        <button onClick={() => { localStorage.clear(); window.location.reload(); }}
          className="px-6 py-2 text-white font-bold rounded-lg" style={{ backgroundColor: accent }}>
          Volver al Login
        </button>
      </div>
    </div>
  );

  const sus = panel.suscripcion;
  const stats = panel.stats || { total_clases: 0, clases_mes: 0 };
  const planes = panel.planes || [];
  const info   = panel.info_gimnasio;

  const statusPill = sus && !sus.esta_vencida ? (
    <Pill variant="accent" accent={accent}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
      Activo
    </Pill>
  ) : (
    <Pill variant="neutral">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
      Sin plan
    </Pill>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-5">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl border bg-slate-900/60 backdrop-blur-xl shadow-[0_20px_60px_-30px_rgba(0,0,0,0.7)]"
          style={{ borderColor: `${accent}22` }}>
          <div className="absolute inset-x-0 top-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
          <div className="p-5 sm:p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                  Hola, {panel.cliente?.first_name || panel.cliente?.username}
                </h2>
                {statusPill}
              </div>
              <p className="mt-1 text-sm text-slate-400">
                {new Date().toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "long" })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* <div className="flex items-center gap-2 rounded-xl border px-3 py-2"
                style={{ borderColor: `${accent}22`, backgroundColor: "rgba(15,23,42,0.35)" }}>
                <div className="grid place-items-center rounded-lg border"
                  style={{ width: 40, height: 40, borderColor: `${accent}35`, backgroundColor: `${accent}12` }}>
                  <TenantLogo tenantConfig={tenantConfig} size="small" />
                </div>
                <div className="hidden sm:block leading-tight">
                  <div className="text-sm font-medium text-white truncate max-w-[180px]">{tenantConfig?.nombre ?? "Gimnasio"}</div>
                  <div className="text-xs text-slate-400">Panel del cliente</div>
                </div>
              </div> */}
              <button onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium text-slate-200 transition hover:text-white"
                style={{ borderColor: "rgba(148,163,184,0.25)", backgroundColor: "rgba(51,65,85,0.35)" }}>
                Cerrar sesión
                <span className="text-xs" style={{ color: accent }}>salir</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Stats ───────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Plan" value={sus ? sus.plan.nombre : "—"} sub={sus ? null : "Sin suscripción"} accent={accent} />
          <StatCard label="Créditos" value={sus ? sus.creditos_restantes : "—"} sub={sus ? `de ${sus.plan.creditos}` : null} accent={accent} />
          <StatCard label="Clases totales" value={stats.total_clases} sub="asistidas" accent={accent} />
          <StatCard label="Este mes" value={stats.clases_mes} sub="clases" accent={accent} />
        </div>

        {/* ── Suscripción ─────────────────────────────────────────────────── */}
        <GlassCard accent={accent} title="Suscripción" subtitle="Detalles de tu plan"
          right={
            sus ? (
              sus.esta_vencida ? <Pill variant="danger">Vencida</Pill>
              : sus.dias_restantes <= 5 ? <Pill variant="warning">Vence pronto</Pill>
              : <Pill variant="accent" accent={accent}>Activa</Pill>
            ) : <Pill variant="neutral">Sin suscripción</Pill>
          }>
          {sus?.pago_pendiente && (
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
              <span className="text-amber-400 text-lg">⚠️</span>
              <p className="text-sm text-amber-200">Tu suscripción tiene un <strong>pago pendiente</strong>. Acercate a recepción.</p>
            </div>
          )}
          {sus ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border p-4" style={{ borderColor: `${accent}22`, backgroundColor: "rgba(15,23,42,0.35)" }}>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Plan</p>
                <p className="mt-1 text-xl font-bold text-white">{sus.plan.nombre}</p>
              </div>
              <div className="rounded-xl border p-4" style={{ borderColor: `${accent}22`, backgroundColor: "rgba(15,23,42,0.35)" }}>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Créditos</p>
                <div className="mt-1 flex items-end justify-between">
                  <p className="text-xl font-bold text-white">{sus.creditos_restantes}</p>
                  <span className="text-xs" style={{ color: accent }}>de {sus.plan.creditos}</span>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-slate-700/60">
                  <div className="h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.max(0, Math.min(100, (sus.creditos_restantes / sus.plan.creditos) * 100))}%`,
                      backgroundColor: sus.creditos_restantes === 0 ? "#ef4444" : accent }} />
                </div>
              </div>
              <div className="rounded-xl border p-4" style={{ borderColor: `${accent}22`, backgroundColor: "rgba(15,23,42,0.35)" }}>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Vence</p>
                <p className="mt-1 text-lg font-bold text-white">
                  {new Date(sus.fecha_fin).toLocaleDateString("es-AR", { day: "2-digit", month: "long" })}
                </p>
                {sus.esta_vencida
                  ? <p className="text-xs text-red-400 mt-1">Venció hace {Math.abs(sus.dias_restantes)} días</p>
                  : sus.dias_restantes === 0
                  ? <p className="text-xs text-red-400 mt-1">Vence hoy</p>
                  : sus.dias_restantes <= 5
                  ? <p className="text-xs text-amber-400 mt-1">Quedan {sus.dias_restantes} días</p>
                  : <p className="text-xs text-slate-500 mt-1">Quedan {sus.dias_restantes} días</p>
                }
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-700 bg-slate-800/30 p-6 text-center">
              <p className="text-slate-300 font-medium">No tenés una suscripción activa</p>
              <p className="text-sm text-slate-500 mt-1">Consultá en recepción para activar un plan.</p>
            </div>
          )}
        </GlassCard>

        {/* ── Planes (solo sin suscripción) ───────────────────────────────── */}
        {!sus && <PlanesSection planes={planes} accent={accent} />}

        {/* ── Badges ──────────────────────────────────────────────────────── */}
        {stats.total_clases > 0 && <BadgesSection stats={stats} accent={accent} />}

        {/* ── Clases reservadas ────────────────────────────────────────────── */}
        <GlassCard accent={accent} title="Clases reservadas" subtitle="Tus próximas reservas confirmadas"
          right={<Pill variant="neutral">{clases_reservadas.length} en agenda</Pill>}>
          {clases_reservadas.length > 0 ? (
            <div className="space-y-3">
              {clases_reservadas.map(c => (
                <div key={c.id} className="rounded-xl border border-slate-700 bg-slate-800/30 p-4 hover:bg-slate-800/45 transition">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-base font-semibold text-white">{c.tipo}</h4>
                        <Pill variant="success"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />Confirmada</Pill>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <span style={{ color: accent }} className="font-medium">{fmtFecha(c.fecha_hora)}</span>
                        <span className="text-slate-600">•</span>
                        <span className="text-slate-400">{fmtHora(c.fecha_hora)}</span>
                      </div>
                      <NotaClase nota={c.nota} accent={accent} />
                    </div>
                    <button onClick={() => handleCancelar(c.id)}
                      className="inline-flex items-center justify-center rounded-xl border border-red-500/30 bg-red-600/10 px-4 py-2 text-sm font-medium text-red-300 hover:bg-red-600/20 transition">
                      Cancelar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-700 bg-slate-800/30 p-6 text-center">
              <p className="text-slate-300 font-medium">No tenés clases reservadas</p>
              <p className="text-sm text-slate-500 mt-1">Reservá una clase para que aparezca acá.</p>
            </div>
          )}
        </GlassCard>

        {/* ── Clases disponibles ───────────────────────────────────────────── */}
        <GlassCard accent={accent} title="Clases disponibles" subtitle="Próximos 7 días"
          right={
            <div className="flex items-center gap-2">
              <Pill variant="neutral">{clasesFiltradas.length} opciones</Pill>
              <button onClick={() => setVista(v => v === "lista" ? "calendario" : "lista")}
                className="text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-600 text-slate-300 hover:text-white transition">
                {vista === "lista" ? "📅 Calendario" : "☰ Lista"}
              </button>
            </div>
          }>
          {/* Filtros */}
          <div className="space-y-3 mb-5">
            <div>
              <p className="text-xs text-slate-500 mb-2">Tipo de clase</p>
              <FiltrosPill
                options={tipos.map(t => ({ value: t, label: t === "todos" ? "Todas" : t }))}
                value={filterTipo} onChange={setFilterTipo} accent={accent} />
            </div>
            {filterTipo === "todos" && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Día</p>
                <FiltrosPill
                  options={diasOpts.map(d => ({ value: d, label: d === "todos" ? "Todos" : d }))}
                  value={filterDia} onChange={setFilterDia} accent={accent} />
              </div>
            )}
          </div>

          {/* Lista o Calendario */}
          {vista === "calendario" ? (
            <CalendarioSemanal
              clases={clasesFiltradas} accent={accent}
              onReservar={handleReservar} suscripcion={sus}
              wishlist={wishlist} toggleWish={toggleWish} />
          ) : clasesFiltradas.length > 0 ? (
            <div className="space-y-3">
              {clasesFiltradas.map(c => {
                const inscritos = c.inscritos_count ?? c.inscritos?.length ?? 0;
                const cuposR = c.cupos_restantes ?? (c.cupos - inscritos);
                const sinSus  = !sus;
                const sinCred = !!sus && sus.creditos_restantes <= 0;
                const llena   = cuposR <= 0;
                const disabled = sinSus || sinCred || llena;
                const wished  = wishlist.has(c.id);
                const label   = llena ? "Clase completa" : sinSus ? "Necesitás suscripción" : sinCred ? "Sin créditos" : "Reservar";

                return (
                  <div key={c.id} className="rounded-xl border border-slate-700 bg-slate-800/30 p-4 hover:bg-slate-800/45 transition">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-base font-semibold text-white">{c.tipo}</h4>
                          <Pill variant={llena ? "danger" : cuposR <= 3 ? "warning" : "success"}>
                            {llena ? "Sin cupos" : `${cuposR} cupos`}
                          </Pill>
                          <button onClick={() => toggleWish(c.id)}
                            className="text-lg leading-none ml-auto sm:ml-0" title={wished ? "Quitar favorito" : "Guardar favorito"}>
                            {wished ? "⭐" : "☆"}
                          </button>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                          <span style={{ color: accent }} className="font-medium">{fmtFecha(c.fecha_hora)}</span>
                          <span className="text-slate-600">•</span>
                          <span className="text-slate-400">{fmtHora(c.fecha_hora)}</span>
                        </div>
                        <CuposBar inscritos={inscritos} cupos={c.cupos} accent={accent} />
                        <NotaClase nota={c.nota} accent={accent} />
                      </div>
                      <PrimaryBtn accent={accent} disabled={disabled} onClick={() => handleReservar(c.id)} title={disabled ? label : "Reservar tu lugar"}>
                        {label}
                      </PrimaryBtn>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-700 bg-slate-800/30 p-6 text-center">
              <p className="text-slate-300 font-medium">No hay clases con estos filtros</p>
              <button onClick={() => { setFilterTipo("todos"); setFilterDia("todos"); }}
                className="text-xs mt-2" style={{ color: accent }}>Limpiar filtros</button>
            </div>
          )}
        </GlassCard>

        {/* ── Historial ───────────────────────────────────────────────────── */}
        <HistorialClases accent={accent} />

        {/* ── Info del gimnasio ────────────────────────────────────────────── */}
        {/* <GimnasioInfo info={info} accent={accent} /> */}

      </div>
    </div>
  );
}
